// hooks/usePostThread.ts — 1.24.00 C2: the thread a post belongs to.
//
// Pass `undefined` for a post that is not in a thread and nothing is fetched —
// that is the 1.24.00 C2 fix-round rule, and PostDetailPage applies it from
// `post.thread`. Every member of a thread carries that field, the ROOT included
// (position 1), so the payload already answers "does this post have parts?" and
// the request is only worth making when it says yes. It used to fire on every
// post-detail view, stand-alone posts included, which is nearly all of them.
//
// Components must NOT import postsService directly; that's what this is for.

import { useEffect, useState } from 'react';
import { postsService } from '@/core/services/postsService';
import type { Post } from '@/core/models/post';

export interface PostThreadHook {
  /**
   * The thread's first post. Null while loading, when there is no thread, AND
   * when the root itself is gone — `rootUnavailable` is what tells the last of
   * those apart from the other two.
   */
  root: Post | null;
  /**
   * The root was soft-deleted or Council-removed, and `parts` are the posts
   * under it that survived (see PostThreadResponse). Read from the server's
   * boolean, never inferred from `root === null`.
   */
  rootUnavailable: boolean;
  /** Parts in position order. Empty for an ordinary post — the common case. */
  parts: Post[];
  loading: boolean;
}

interface ThreadState extends PostThreadHook {
  /** Which identifier `root`/`parts` belong to. */
  ident: string | undefined;
}

function empty(ident: string | undefined): ThreadState {
  return { ident, root: null, rootUnavailable: false, parts: [], loading: Boolean(ident) };
}

export function usePostThread(ident: string | undefined): PostThreadHook {
  const [state, setState] = useState<ThreadState>(() => empty(ident));

  // Reset DURING render rather than in the effect (React's documented "adjust
  // state when a prop changes" pattern, the same one Composer uses for its
  // draft handoffs): navigating from one thread's part straight to an unrelated
  // post must not leave the previous thread on screen for a frame, attached to
  // a post it has nothing to do with.
  if (state.ident !== ident) setState(empty(ident));

  useEffect(() => {
    if (!ident) return;
    let cancelled = false;
    postsService
      .postThread(ident)
      .then((data) => {
        if (cancelled) return;
        setState({
          ident,
          root: data?.root ?? null,
          // The VALUE, not the key: a 404 (data === null) and a server that
          // predates this field both mean "an ordinary thread or none at all",
          // and neither should raise a tombstone.
          rootUnavailable: data?.rootUnavailable === true,
          parts: data?.parts ?? [],
          loading: false,
        });
      })
      .catch(() => {
        if (!cancelled)
          setState({ ident, root: null, rootUnavailable: false, parts: [], loading: false });
      });
    return () => {
      cancelled = true;
    };
  }, [ident]);

  return {
    root: state.root,
    rootUnavailable: state.rootUnavailable,
    parts: state.parts,
    loading: state.loading,
  };
}
